import React, { useLayoutEffect, useRef, useState } from "react";
import { useIsMobile } from "./useMediaQuery";
import "./PageToolbar.css";

/**
 * Search/filter + actions bar above a list. Desktop: one row, filters start, actions end.
 * Phones: filters stay on top (sticky); a single primary action turns into a floating action
 * button above the bottom navigation, several actions share one row.
 *
 *   <PageToolbar
 *     filters={<Input.Search ... />}
 *     actions={<Button type="primary" icon={<PlusOutlined />}>{t("Add")}</Button>}
 *   />
 */
const PageToolbar = ({ filters, actions, children, className = "" }) => {
  const isMobile = useIsMobile();
  const actionsRef = useRef(null);
  const [fab, setFab] = useState(false);

  // Decided from the DOM because `actions` is usually a component (e.g. <AddEmployee />) that
  // renders its own button plus a dialog.
  useLayoutEffect(() => {
    const el = actionsRef.current;
    if (!isMobile || !el || el.closest(".ant-modal, .ant-drawer")) {
      setFab(false);
      return undefined;
    }
    const check = () => {
      const buttons = el.querySelectorAll(".ant-btn, button, a[href]");
      setFab(buttons.length === 1 && buttons[0].classList.contains("ant-btn-primary"));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isMobile, actions]);

  const hasFilters = Boolean(filters || children);

  return (
    <div
      className={`page-toolbar${fab ? " page-toolbar--fab" : ""}${
        hasFilters ? "" : " page-toolbar--no-filters"
      } ${className}`}
    >
      {hasFilters && <div className="page-toolbar__filters">{filters ?? children}</div>}
      {actions && (
        <div className="page-toolbar__actions" ref={actionsRef}>
          {actions}
        </div>
      )}
    </div>
  );
};

export default PageToolbar;
